import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import type { Booking } from "@/types/camping";

export interface PlaceBanConfirmModalProps {
  open: boolean;
  booking: Booking | null;
  loading?: boolean;
  onClose: () => void;
  onProceed: (liftPlaceBan: boolean) => void | Promise<void>;
}

export function PlaceBanConfirmModal({
  open,
  booking,
  loading = false,
  onClose,
  onProceed,
}: PlaceBanConfirmModalProps) {
  const [liftPlaceBan, setLiftPlaceBan] = useState(false);

  useEffect(() => {
    if (open) setLiftPlaceBan(false);
  }, [open, booking?.id]);

  if (!booking) return null;

  const matchedName = booking.place_ban_matched_guest_name;
  const reason = booking.place_ban_reason;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Platzverbot – Bestätigung prüfen"
      description={`Anfrage ${booking.booking_number}: Möchten Sie trotz erkanntem Platzverbot fortfahren?`}
      size="md"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button
            type="button"
            loading={loading}
            disabled={loading}
            onClick={() => void onProceed(liftPlaceBan)}
          >
            Fortfahren
          </Button>
          <Button type="button" variant="ghost" className="sm:ml-auto" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border-2 border-status-red/50 bg-status-red/12 px-4 py-4">
          <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-status-red" aria-hidden />
          <div className="space-y-2 text-sm">
            <p className="flex flex-wrap items-center gap-2 font-semibold text-status-red">
              Platzverbot erkannt
              <PlaceBanBadge prominent />
            </p>
            <p className="text-label-secondary">
              Diese Buchung wurde als Anfrage angelegt, weil ein Platzverbot erkannt wurde. Der Gast
              sieht davon nichts. Bitte prüfen Sie die Angaben sorgfältig, bevor Sie eine Zusage
              senden.
            </p>
            {matchedName ? (
              <p>
                <span className="text-label-tertiary">Abgleich mit: </span>
                <span className="font-medium text-label">{matchedName}</span>
              </p>
            ) : null}
            {reason ? (
              <p className="rounded-lg bg-status-red/10 px-3 py-2 text-label">
                <span className="font-medium text-status-red">Begründung: </span>
                {reason}
              </p>
            ) : null}
          </div>
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-separator bg-fill-tertiary/40 px-4 py-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-separator text-accent focus:ring-accent"
            checked={liftPlaceBan}
            onChange={(e) => setLiftPlaceBan(e.target.checked)}
            disabled={loading}
          />
          <span className="text-sm text-label">
            <span className="font-medium">Platzverbot aufheben</span>
            <span className="mt-0.5 block text-label-secondary">
              Entfernt das Platzverbot beim abgeglichenen Gast in der Stammkartei. Empfohlen, wenn
              Sie den Gast wieder aufnehmen möchten.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
